/**
 * The diagnostics an `ErrorBoundary` offers to copy (design §2.12).
 *
 * The correlation id is the whole point: it is the one token that ties what the
 * user saw to the server log lines that produced it. So it is captured at the
 * moment a call is MADE, not only when one fails — a request that never got a
 * response still has an id the server may have logged, and a React render crash
 * downstream of a successful fetch can still name the call that fed it.
 */

import { CLIENT_VERSION } from '../env.ts';
import { toClientError } from './errors.ts';

export type Diagnostics = {
  readonly correlationId: string | null;
  readonly route: string;
  readonly clientVersion: string;
  readonly at: string;
  readonly message: string;
  readonly componentStack?: string;
};

let lastCorrelationId: string | null = null;

/** Records the id minted for (or returned by) the most recent API call. */
export function noteCorrelationId(id: string | null | undefined): void {
  if (typeof id === 'string' && id.length >= 8) {
    lastCorrelationId = id;
  }
}

export function currentCorrelationId(): string | null {
  return lastCorrelationId;
}

/**
 * A fresh correlation id for one logical call.
 *
 * `crypto.randomUUID` rather than a ULID: it is in every target browser, needs
 * no dependency, and satisfies the contract's `min(8).max(64)` header schema.
 * ULIDs are used server-side where lexicographic time ordering earns its keep;
 * here the id is only ever looked up, never sorted.
 */
export function newCorrelationId(): string {
  const uuid = globalThis.crypto?.randomUUID?.();
  if (typeof uuid === 'string') {
    return uuid;
  }
  // A non-secure context (or an old test environment) still needs an id; it only
  // has to be unique enough to find one request in one log stream.
  return `c-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

export function buildDiagnostics(input: {
  error: unknown;
  route: string;
  componentStack?: string | undefined;
}): Diagnostics {
  const client = toClientError(input.error);
  return {
    correlationId: client.correlationId ?? lastCorrelationId,
    route: input.route,
    clientVersion: CLIENT_VERSION,
    at: new Date().toISOString(),
    message: client.message,
    ...(input.componentStack === undefined ? {} : { componentStack: input.componentStack }),
  };
}

export function formatDiagnostics(diagnostics: Diagnostics): string {
  const lines = [
    `correlation-id: ${diagnostics.correlationId ?? '(none — request never reached the server)'}`,
    `route:          ${diagnostics.route}`,
    `client:         web ${diagnostics.clientVersion}`,
    `at:             ${diagnostics.at}`,
    `error:          ${diagnostics.message}`,
  ];
  if (diagnostics.componentStack !== undefined) {
    lines.push('component-stack:', diagnostics.componentStack.trim());
  }
  return lines.join('\n');
}

/**
 * Copies text, falling back to a hidden textarea.
 *
 * `navigator.clipboard` is undefined on a non-secure origin and rejects when the
 * document is not focused — both of which happen exactly when someone is trying
 * to report a bug, so the fallback is not decoration.
 */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (globalThis.navigator?.clipboard?.writeText !== undefined) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through
  }
  try {
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(area);
    return ok;
  } catch {
    return false;
  }
}
